import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ensureListing } from '../../../util/data';
import { requireListingImage } from '../../../util/configHelpers';
import { cardDetails } from '../../../util/cardDetails';

import { AspectRatioWrapper, FittedImage, ListingCardThumbnail } from '../../../components';

import css from './SearchMapInfoCard.module.css';

// Small calendar glyph for the days/dates rows (same shape as the class card's).
const IconDays = () => (
  <svg className={css.rowIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="none">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" fill="none" strokeWidth="2" />
    <path d="M3.5 9.5h17M8 3v4M16 3v4" fill="none" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Small map-pin glyph for the location row (same shape as the class card's).
const IconPin = () => (
  <svg className={css.rowIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="none">
    <path
      d="M12 21s-7-5.1-7-11a7 7 0 1 1 14 0c0 5.9-7 11-7 11z"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" fill="none" strokeWidth="2" />
  </svg>
);

/**
 * The map pop-up's class card: the same details as the search/home class card (cardDetails) in a
 * 250px frame -- ages + format badge on the photo; category, title, days/times, dates, city; then the
 * price with its unit and the provider. No overlay view or carousel controls here.
 */
const ListingCard = props => {
  const { className, clickHandler, intl, isInCarousel, listing, urlToListing, config } = props;

  const { title, publicData } = listing.attributes;
  const { cardStyle } = publicData || {};
  const firstImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === publicData?.listingType
  );
  // KidHive: classes always carry photos -- show the image whenever the listing has one (the
  // config-driven requireListingImage gate hid it when the hosted listing type doesn't flag
  // images); the colored thumbnail stays as the no-image fallback.
  const showListingImage = !!firstImage || requireListingImage(foundListingTypeConfig);

  const {
    cityLabel,
    ageLabel,
    daysLabel,
    timeLabel,
    datesLabel,
    formatLabel,
    isDropInLabel,
    categoryLabel,
    priceFormatted,
    unit,
    authorName,
  } = cardDetails(intl, listing, config.categoryConfiguration);
  const scheduleLabel = daysLabel ? (timeLabel ? `${daysLabel} · ${timeLabel}` : daysLabel) : null;

  // listing card anchor needs sometimes inherited border radius.
  const classes = classNames(
    css.anchor,
    css.borderRadiusInheritTop,
    { [css.borderRadiusInheritBottom]: !isInCarousel },
    className
  );

  return (
    <a
      alt={title}
      className={classes}
      href={urlToListing}
      onClick={e => {
        e.preventDefault();
        // Use clickHandler from props to call internal router
        clickHandler(listing);
      }}
    >
      <div
        className={classNames(css.card, css.borderRadiusInheritTop, {
          [css.borderRadiusInheritBottom]: !isInCarousel,
        })}
      >
        <div className={css.media}>
          {showListingImage ? (
            <AspectRatioWrapper
              className={css.aspectRatioWrapper}
              width={aspectWidth}
              height={aspectHeight}
            >
              {/* The shared fit-not-crop treatment (FittedImage). */}
              <FittedImage
                className={classNames(css.rootForImage, css.borderRadiusInheritTop)}
                alt={title}
                image={firstImage}
                sizes="250px"
              />
            </AspectRatioWrapper>
          ) : (
            <ListingCardThumbnail
              style={cardStyle}
              listingTitle={title}
              className={css.aspectRatioWrapper}
              width={aspectWidth}
              height={aspectHeight}
            />
          )}
          {ageLabel ? <span className={css.ageBadge}>{ageLabel}</span> : null}
          {formatLabel ? (
            <span
              className={classNames(css.formatBadge, { [css.formatBadgeDropIn]: isDropInLabel })}
            >
              {formatLabel}
            </span>
          ) : null}
        </div>
        <div className={classNames(css.info, { [css.borderRadiusInheritBottom]: !isInCarousel })}>
          {categoryLabel ? <span className={css.category}>{categoryLabel}</span> : null}
          <div className={css.name}>{title}</div>
          {scheduleLabel ? (
            <span className={css.row}>
              <IconDays />
              <span className={css.rowText}>{scheduleLabel}</span>
            </span>
          ) : null}
          {datesLabel ? (
            <span className={css.row}>
              <IconDays />
              <span className={css.rowText}>{datesLabel}</span>
            </span>
          ) : null}
          {cityLabel ? (
            <span className={css.row}>
              <IconPin />
              <span className={css.rowText}>{cityLabel}</span>
            </span>
          ) : null}
          {priceFormatted || authorName ? (
            <div className={css.footer}>
              {priceFormatted ? (
                <span className={css.price}>
                  {priceFormatted}
                  {unit ? <span className={css.priceUnit}>{` / ${unit}`}</span> : null}
                </span>
              ) : null}
              {authorName ? <span className={css.author}>{authorName}</span> : null}
            </div>
          ) : null}
        </div>
      </div>
    </a>
  );
};

/**
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {Function} props.onListingInfoCardClicked - The function to handle the listing info card click
 * @param {Function} props.createURLToListing - The function to create the URL to the listing
 * @param {Object} props.config - The configuration
 * @returns {JSX.Element}
 */
const SearchMapInfoCard = props => {
  const [currentListingIndex, setCurrentListingIndex] = useState(0);
  const intl = useIntl();
  const {
    className,
    rootClassName,
    listings,
    createURLToListing,
    onListingInfoCardClicked,
    config,
  } = props;
  const currentListing = ensureListing(listings[currentListingIndex]);
  const hasCarousel = listings.length > 1;

  const classes = classNames(rootClassName || css.root, className);
  const caretClass = classNames(css.caret, { [css.caretWithCarousel]: hasCarousel });

  return (
    <div className={classes}>
      <div className={css.caretShadow} />
      <ListingCard
        clickHandler={onListingInfoCardClicked}
        urlToListing={createURLToListing(currentListing)}
        listing={currentListing}
        intl={intl}
        isInCarousel={hasCarousel}
        config={config}
      />
      {hasCarousel ? (
        <div className={classNames(css.paginationInfo, css.borderRadiusInheritBottom)}>
          <button
            className={css.paginationPrev}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length - 1) % listings.length
              );
            }}
          />
          <div className={css.paginationPage}>
            {`${currentListingIndex + 1}/${listings.length}`}
          </div>
          <button
            className={css.paginationNext}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length + 1) % listings.length
              );
            }}
          />
        </div>
      ) : null}
      <div className={caretClass} />
    </div>
  );
};

export default SearchMapInfoCard;
